import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuApi, checksApi, ordersApi, tablesApi } from '../services/api';
import { useOrderStore, mapServerOrderItems } from '../stores/orderStore';
import { useAuthStore } from '../stores/authStore';
import { offlineService, useOnlineStatus } from '../services/offline';
import { printerService } from '../services/printer';
import { useToast } from '../components/Toast';
import ModifierModal from '../components/ModifierModal';
import GuestRecognition from '../components/GuestRecognition';
import CourseManager from '../components/CourseManager';
import CourseDragEditor from '../components/CourseDragEditor';
import {
  ArrowLeft,
  Send,
  CreditCard,
  Plus,
  Minus,
  Trash2,
  Users,
  ChefHat,
  Search,
  XCircle,
  ShoppingCart,
  X,
  AlertTriangle,
  Flame,
  Clock,
  Layers,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import clsx from 'clsx';

interface MenuItem {
  id: string;
  name: string;
  shortName?: string;
  description?: string;
  priceCents: number;
  isAvailable: boolean;
  allergens?: string[];
  tags?: string[];
  modifierGroups: Array<{
    id: string;
    name: string;
    required: boolean;
    minSelections: number;
    maxSelections: number;
    modifiers: Array<{
      id: string;
      name: string;
      priceAdjustmentCents: number;
    }>;
  }>;
}

// Common allergen icons/labels
const ALLERGEN_LABELS: Record<string, { label: string; color: string }> = {
  'gluten': { label: 'G', color: 'bg-amber-500' },
  'dairy': { label: 'D', color: 'bg-blue-500' },
  'nuts': { label: 'N', color: 'bg-orange-600' },
  'peanuts': { label: 'P', color: 'bg-orange-700' },
  'shellfish': { label: 'SF', color: 'bg-red-500' },
  'fish': { label: 'F', color: 'bg-cyan-500' },
  'eggs': { label: 'E', color: 'bg-yellow-500' },
  'soy': { label: 'S', color: 'bg-green-600' },
  'sesame': { label: 'SE', color: 'bg-amber-600' },
};

interface Category {
  id: string;
  name: string;
  items: MenuItem[];
}

export default function OrderPage() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [showAllergenModal, setShowAllergenModal] = useState(false);
  const [guestAllergens, setGuestAllergens] = useState<string[]>([]);

  // Common allergen options
  const ALLERGEN_OPTIONS = ['Gluten', 'Dairy', 'Nuts', 'Peanuts', 'Shellfish', 'Fish', 'Eggs', 'Soy', 'Sesame'];
  const { isOnline } = useOnlineStatus();
  const { employee } = useAuthStore();
  const { showError, showSuccess } = useToast();

  const {
    tableNumber,
    checkId,
    items,
    partySize,
    currentCourse,
    setCheckId,
    setGuest,
    setPartySize,
    setCurrentCourse,
    addItem,
    updateItemQuantity,
    updateItemCourse,
    removeItem,
    loadItems,
    getSubtotal,
    getTax,
    getTotal,
    getItemsByCourse,
    getMaxCourse,
    clearItems,
    clearOrder,
  } = useOrderStore();

  // Course mode: 'immediate' fires all at once, 'coursed' holds items until fired
  const [courseMode, setCourseMode] = useState<'immediate' | 'coursed'>('immediate');
  const [showCourseManager, setShowCourseManager] = useState(false);

  // Fetch menu
  const { data: menuData, isLoading: menuLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: async () => {
      const response = await menuApi.getAll();
      return response.data as Category[];
    },
  });

  // Fetch existing check data when resuming an occupied table
  const { data: existingCheck } = useQuery({
    queryKey: ['check', checkId],
    queryFn: async () => {
      if (!checkId) return null;
      const response = await checksApi.getOne(checkId);
      return response.data;
    },
    enabled: !!checkId && items.length === 0,
  });

  // Load items from existing check — via the shared server-item mapper so the
  // cart, By Course tab, and Checkout all normalize prices/modifiers the same
  // way (server unit prices include modifiers; local math adds them on top).
  useEffect(() => {
    if (existingCheck?.orders && items.length === 0) {
      const allItems = mapServerOrderItems(existingCheck.orders);
      if (allItems.length > 0) {
        loadItems(allItems);
      }
      if (existingCheck.partySize) {
        setPartySize(existingCheck.partySize);
      }
      if (existingCheck.guestAllergens) {
        setGuestAllergens(existingCheck.guestAllergens);
      }
      // Load guest info for receipts
      if (existingCheck.guestId || existingCheck.guestName) {
        setGuest(existingCheck.guestId || null, existingCheck.guestName || null);
      }
    }
  }, [existingCheck, items.length, loadItems, setPartySize, setGuest]);

  // Update allergens mutation
  const updateAllergensMutation = useMutation({
    mutationFn: async (allergens: string[]) => {
      if (!checkId) return;
      return await checksApi.update(checkId, { guestAllergens: allergens });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
    },
  });

  const toggleAllergen = (allergen: string) => {
    const newAllergens = guestAllergens.includes(allergen)
      ? guestAllergens.filter(a => a !== allergen)
      : [...guestAllergens, allergen];
    setGuestAllergens(newAllergens);
    if (checkId) {
      updateAllergensMutation.mutate(newAllergens);
    }
  };

  // Send order mutation
  const sendOrderMutation = useMutation({
    mutationFn: async () => {
      if (!isOnline) {
        // Same filter as the online path: only NEW items — queueing the whole
        // cart re-sends already-sent items on replay (double charge + recook)
        const newItems = items.filter((item) => item.holdUntilFired === undefined);
        if (newItems.length === 0) {
          throw new Error('No new items to send');
        }
        const offlineOrderId = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        await offlineService.queueOrder({
          id: offlineOrderId,
          checkId: checkId || `pending-check-${tableId}`,
          tableId: tableId!,
          fireImmediately: courseMode === 'immediate',
          items: newItems.map((item) => ({
            menuItemId: item.menuItemId,
            name: item.name,
            quantity: item.quantity,
            unitPriceCents: item.priceCents,
            seatNumber: item.seatNumber,
            courseNumber: item.courseNumber,
            modifiers: item.modifiers,
            specialInstructions: item.specialInstructions,
          })),
          createdAt: new Date().toISOString(),
        });
        return { id: offlineOrderId, offline: true };
      }

      let currentCheckId = checkId;
      if (!currentCheckId) {
        const checkResponse = await checksApi.open({
          tableId: tableId!,
          partySize,
          guestAllergens: guestAllergens.length > 0 ? guestAllergens : undefined,
        });
        if (!checkResponse.success || !checkResponse.data?.id) {
          throw new Error(checkResponse.error?.message || 'Failed to create check');
        }
        currentCheckId = checkResponse.data.id;
        setCheckId(currentCheckId);
      }

      // Only send new/unsaved items (holdUntilFired === undefined means local-only)
      const itemsToSend = items.filter(item => item.holdUntilFired === undefined);

      if (itemsToSend.length === 0) {
        throw new Error('No new items to send');
      }

      const orderResponse = await ordersApi.create({
        checkId: currentCheckId,
        items: itemsToSend.map((item) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          seatNumber: item.seatNumber,
          courseNumber: item.courseNumber,
          modifiers: item.modifiers?.map(m => ({
            modifierId: m.id,
            name: m.name,
            priceAdjustment: m.priceCents || 0,
          })),
          specialInstructions: item.specialInstructions,
        })),
        // In coursed mode, don't fire immediately - hold until course is fired
        fireImmediately: courseMode === 'immediate',
      });

      return orderResponse.data;
    },
    onSuccess: (data) => {
      if (courseMode === 'immediate') {
        // Only print newly sent items (not items already in kitchen)
        const sentItems = items.filter(item => item.holdUntilFired === undefined);
        printerService.printKitchenTicket({
          orderNumber: data.id || 'N/A',
          tableNumber: tableNumber || tableId || 'N/A',
          items: sentItems.map((item) => ({
            name: item.shortName || item.name,
            quantity: item.quantity,
            modifiers: item.modifiers,
            specialInstructions: item.specialInstructions,
            courseNumber: item.courseNumber,
          })),
          serverName: employee ? `${employee.firstName} ${employee.lastName}` : undefined,
          createdAt: new Date(),
        }).catch((err) => console.error('Failed to print kitchen ticket:', err));

        showSuccess('Order sent to kitchen');
        clearOrder();
        navigate('/tables');
      } else {
        // Coursed mode - order is held, stay on page to manage courses
        showSuccess('Order saved - fire courses when ready');
        // Clear local items (keep checkId) so we reload from server for course management
        clearItems();
        // Reload check data to get the new order items from server
        queryClient.invalidateQueries({ queryKey: ['check', checkId] });
      }
    },
    onError: (error: Error) => {
      console.error('Failed to send order:', error);
      showError(
        error.message || 'Failed to send order. Please try again.',
        {
          label: 'Retry',
          onClick: () => sendOrderMutation.mutate(),
        }
      );
    },
  });

  // Fire course mutation
  const fireCourseMutation = useMutation({
    mutationFn: async (courseNumber: number) => {
      if (!checkId) throw new Error('No check ID');
      const response = await ordersApi.fireCourse(checkId, courseNumber);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fire course');
      }
      return response.data;
    },
    onSuccess: (data, courseNumber) => {
      showSuccess(`Course ${courseNumber} fired to kitchen`);
      // Clear items so they reload from server with updated statuses
      clearItems();
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
    },
    onError: (error: Error) => {
      console.error('Failed to fire course:', error);
      showError(error.message || 'Failed to fire course');
    },
  });

  // Void a sent item (server-authoritative — local qty edits would desync).
  // Voids may need manager approval: on MANAGER_AUTH_REQUIRED we ask for a
  // manager PIN in the same modal instead of dead-ending a non-manager.
  const [voidTarget, setVoidTarget] = useState<{ id: string; name: string } | null>(null);
  const [voidNeedsPin, setVoidNeedsPin] = useState<string | null>(null); // holds the chosen reason
  const [voidPin, setVoidPin] = useState('');
  const voidItemMutation = useMutation({
    mutationFn: async ({ itemId, reason, managerPin }: { itemId: string; reason: string; managerPin?: string }) => {
      const response = await ordersApi.voidItem(itemId, reason, managerPin);
      if (!response.success) {
        if (response.error?.code === 'MANAGER_AUTH_REQUIRED') {
          throw Object.assign(new Error('MANAGER_AUTH_REQUIRED'), { reason });
        }
        throw new Error(response.error?.message || "Couldn't void the item");
      }
      return response.data;
    },
    onSuccess: () => {
      showSuccess('Item removed from the order');
      setVoidTarget(null);
      setVoidNeedsPin(null);
      setVoidPin('');
      clearItems();
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
    },
    onError: (error: any) => {
      if (error.message === 'MANAGER_AUTH_REQUIRED') {
        setVoidNeedsPin(error.reason);
        return;
      }
      setVoidPin('');
      showError(error.message || "Couldn't void the item");
    },
  });

  // Set initial category
  useEffect(() => {
    if (menuData?.length && !selectedCategory) {
      setSelectedCategory(menuData[0].id);
    }
  }, [menuData, selectedCategory]);

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const currentCategory = menuData?.find((c) => c.id === selectedCategory);

  const filteredItems = searchQuery
    ? menuData
        ?.flatMap((c) => c.items)
        .filter(
          (item) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.shortName?.toLowerCase().includes(searchQuery.toLowerCase())
        )
    : currentCategory?.items;

  const [modifierModalItem, setModifierModalItem] = useState<MenuItem | null>(null);

  const handleAddItem = (item: MenuItem) => {
    if (item.modifierGroups && item.modifierGroups.length > 0) {
      setModifierModalItem(item);
    } else {
      addItem({
        menuItemId: item.id,
        name: item.name,
        shortName: item.shortName,
        priceCents: item.priceCents,
        modifiers: [],
      });
    }
  };

  const handleModifierConfirm = (modifiers: Array<{ id: string; name: string; priceCents: number }>, specialInstructions: string) => {
    if (!modifierModalItem) return;

    addItem({
      menuItemId: modifierModalItem.id,
      name: modifierModalItem.name,
      shortName: modifierModalItem.shortName,
      priceCents: modifierModalItem.priceCents,
      modifiers,
      specialInstructions: specialInstructions || undefined,
    });

    setModifierModalItem(null);
  };

  const handleSendOrder = () => {
    if (items.length === 0) return;
    sendOrderMutation.mutate();
    setShowMobileCart(false);
  };

  const handleCheckout = () => {
    if (items.length === 0) return;
    setShowMobileCart(false);
    navigate(`/order/${tableId}/checkout`);
  };

  // Clear table mutation
  const clearTableMutation = useMutation({
    mutationFn: async () => {
      if (!tableId) throw new Error('No table ID');
      if (checkId) {
        try {
          await checksApi.close(checkId);
        } catch (e) {
          console.log('Check already closed or not found');
        }
      }
      const response = await tablesApi.clear(tableId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to clear table');
      }
      if (!response.success) {
        throw new Error('Failed to clear table');
      }
      return response.data;
    },
    onSuccess: async () => {
      clearOrder();
      await queryClient.invalidateQueries({ queryKey: ['tables'] });
      navigate('/tables');
    },
    onError: (error: Error) => {
      console.error('Failed to clear table:', error);
      showError(error?.message || 'Failed to clear table. Please try again.');
    },
  });

  const handleClearTable = () => {
    if (window.confirm('Clear this table? This will close any open check.')) {
      clearTableMutation.mutate();
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header - Compact for mobile */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-3 py-2 flex items-center gap-2">
          <button
            onClick={() => navigate('/tables')}
            className="p-1.5 -ml-1 rounded-lg hover:bg-slate-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-base">Table {tableNumber}</h2>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={partySize}
                  onChange={(e) => setPartySize(parseInt(e.target.value) || 1)}
                  className="w-8 px-1 py-0.5 border rounded text-center text-xs"
                />
              </div>
              {/* Allergen Button */}
              <button
                onClick={() => setShowAllergenModal(true)}
                className={clsx(
                  'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                  guestAllergens.length > 0
                    ? 'bg-red-100 text-red-700 border border-red-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                <AlertTriangle className="w-3 h-3" />
                {guestAllergens.length > 0 ? `${guestAllergens.length} Allergies` : 'Allergies'}
              </button>
              {/* Course Manager Button - show when there's an existing check */}
              {checkId && (
                <button
                  onClick={() => setShowCourseManager(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200 transition-colors"
                >
                  <Layers className="w-3 h-3" />
                  Courses
                </button>
              )}
            </div>
          </div>

          <button
            onClick={handleClearTable}
            disabled={clearTableMutation.isPending}
            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 pl-8 pr-3 bg-slate-100 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Categories - Scrollable */}
        {!searchQuery && (
          <div className="px-3 pb-2 overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5 min-w-max">
              {menuData?.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={clsx(
                    'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                  )}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Guest Recognition Banner */}
      {existingCheck?.guestId && (
        <div className="px-3 pt-2">
          <GuestRecognition
            guestId={existingCheck.guestId}
            guestName={existingCheck.guestName}
            compact={true}
          />
        </div>
      )}

      {/* Menu Items Grid */}
      <div className="flex-1 overflow-y-auto p-2 scrollbar-hide scroll-bounce">
        {menuLoading ? (
          <div className="text-center py-8 text-slate-500 text-sm">Loading menu...</div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredItems?.map((item) => (
              <button
                key={item.id}
                onClick={() => handleAddItem(item)}
                disabled={!item.isAvailable}
                className={clsx(
                  'bg-white rounded-xl p-3 text-left shadow-sm border border-slate-100 relative',
                  'active:scale-95 transition-transform',
                  !item.isAvailable && 'opacity-50',
                  item.allergens && item.allergens.length > 0 && 'border-amber-300'
                )}
              >
                {/* Allergen badges */}
                {item.allergens && item.allergens.length > 0 && (
                  <div className="flex gap-0.5 mb-1.5 flex-wrap">
                    {item.allergens.slice(0, 4).map((allergen) => {
                      const config = ALLERGEN_LABELS[allergen.toLowerCase()] || { label: allergen.charAt(0).toUpperCase(), color: 'bg-slate-500' };
                      return (
                        <span
                          key={allergen}
                          className={clsx('text-[9px] text-white font-bold px-1.5 py-0.5 rounded', config.color)}
                          title={allergen}
                        >
                          {config.label}
                        </span>
                      );
                    })}
                    {item.allergens.length > 4 && (
                      <span className="text-[9px] text-slate-500 font-medium">+{item.allergens.length - 4}</span>
                    )}
                  </div>
                )}
                <p className="font-medium text-sm text-slate-900 line-clamp-2 mb-1">
                  {item.shortName || item.name}
                </p>
                <p className="text-sm font-semibold text-primary-600">
                  {formatCurrency(item.priceCents)}
                </p>
                {!item.isAvailable && (
                  <span className="text-[10px] text-red-500 font-medium">86'd</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile Cart Button */}
      {items.length > 0 && !showMobileCart && (
        <div className="p-3 bg-white border-t border-slate-200">
          <button
            onClick={() => setShowMobileCart(true)}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-xl flex items-center justify-between shadow-lg"
          >
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-full w-6 h-6 flex items-center justify-center">
                <span className="text-sm font-bold">{items.reduce((sum, i) => sum + i.quantity, 0)}</span>
              </div>
              <span className="font-medium">View Order</span>
            </div>
            <span className="font-bold">{formatCurrency(getTotal())}</span>
          </button>
        </div>
      )}

      {/* Mobile Cart Overlay */}
      {showMobileCart && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col safe-top safe-bottom">
          {/* Cart Header */}
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-semibold text-lg">Your Order</h3>
                <p className="text-sm text-slate-500">Table {tableNumber} • {items.reduce((sum, i) => sum + i.quantity, 0)} items</p>
              </div>
              <button
                onClick={() => setShowMobileCart(false)}
                className="p-2 hover:bg-slate-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Course Mode Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setCourseMode('immediate')}
                className={clsx(
                  'flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors',
                  courseMode === 'immediate'
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                )}
              >
                <Send className="w-4 h-4" />
                Fire All
              </button>
              <button
                onClick={() => setCourseMode('coursed')}
                className={clsx(
                  'flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors',
                  courseMode === 'coursed'
                    ? 'bg-amber-600 text-white'
                    : 'bg-slate-100 text-slate-600'
                )}
              >
                <Layers className="w-4 h-4" />
                By Course
              </button>
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50 scroll-bounce">
            {items.length === 0 ? (
              <div className="text-center text-slate-500 py-12">
                <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No items yet</p>
                <p className="text-sm">Tap menu items to add</p>
              </div>
            ) : courseMode === 'coursed' ? (
              // Coursed mode - drag and drop courses
              <CourseDragEditor
                items={items}
                onUpdateItemCourse={updateItemCourse}
                onUpdateItemQuantity={updateItemQuantity}
                onFireCourse={(courseNum) => fireCourseMutation.mutate(courseNum)}
                isFirePending={fireCourseMutation.isPending}
                checkId={checkId}
                formatCurrency={formatCurrency}
              />
            ) : (
              // Immediate mode - flat list
              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">
                          {item.shortName || item.name}
                        </p>
                        {item.modifiers.length > 0 && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {item.modifiers.map((m) => m.name).join(', ')}
                          </p>
                        )}
                        {item.specialInstructions && (
                          <p className="text-xs text-amber-600 mt-0.5">
                            {item.specialInstructions}
                          </p>
                        )}
                      </div>
                      <p className="font-semibold text-slate-900">
                        {formatCurrency(item.priceCents * item.quantity)}
                      </p>
                    </div>

                    {item.status ? (
                      /* Already on the server — qty edits here would desync;
                         removal goes through a proper void instead */
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                          {item.kitchenStatus === 'queued' || item.status === 'sent' ? 'Sent to kitchen' : 'On the check'}
                        </span>
                        <button
                          onClick={() => setVoidTarget({ id: item.id, name: item.shortName || item.name })}
                          className="text-xs font-medium text-red-500 px-2 py-1 rounded-lg active:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:bg-slate-200"
                          >
                            {item.quantity === 1 ? (
                              <Trash2 className="w-4 h-4 text-red-500" />
                            ) : (
                              <Minus className="w-4 h-4" />
                            )}
                          </button>
                          <span className="w-6 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:bg-slate-200"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-xs text-slate-400">
                          {formatCurrency(item.priceCents)} each
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Totals */}
          {items.length > 0 && (
            <div className="bg-white border-t border-slate-200">
              <div className="p-4 space-y-1">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span>
                  <span>{formatCurrency(getSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Tax</span>
                  <span>{formatCurrency(getTax())}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
                  <span>Total</span>
                  <span>{formatCurrency(getTotal())}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 pt-0 space-y-2">
                {courseMode === 'immediate' ? (
                  <button
                    onClick={handleSendOrder}
                    disabled={items.length === 0 || sendOrderMutation.isPending}
                    className="w-full bg-primary-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 active:bg-primary-700 disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                    {sendOrderMutation.isPending ? 'Sending...' : 'Send to Kitchen'}
                  </button>
                ) : (
                  (() => {
                    // Context-aware button text for coursed mode
                    const unsavedItems = items.filter(i => i.holdUntilFired === undefined);
                    const hasExistingOrder = items.some(i => i.holdUntilFired !== undefined);

                    let buttonText = 'Hold Order (Fire by Course)';
                    let ButtonIcon = Clock;

                    if (sendOrderMutation.isPending) {
                      buttonText = 'Saving...';
                    } else if (hasExistingOrder && unsavedItems.length > 0) {
                      // Adding items to existing order
                      buttonText = `Add ${unsavedItems.length} Item${unsavedItems.length > 1 ? 's' : ''} to Order`;
                      ButtonIcon = Plus;
                    } else if (unsavedItems.length === items.length) {
                      // All items are new, no existing order yet
                      buttonText = 'Hold Order (Fire by Course)';
                    }

                    return (
                      <button
                        onClick={handleSendOrder}
                        disabled={unsavedItems.length === 0 || sendOrderMutation.isPending}
                        className="w-full bg-amber-600 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 active:bg-amber-700 disabled:opacity-50"
                      >
                        <ButtonIcon className="w-5 h-5" />
                        {buttonText}
                      </button>
                    );
                  })()
                )}
                <button
                  onClick={handleCheckout}
                  disabled={items.length === 0}
                  className="w-full bg-slate-100 text-slate-700 py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 active:bg-slate-200 disabled:opacity-50"
                >
                  <CreditCard className="w-5 h-5" />
                  Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modifier Modal */}
      {modifierModalItem && (
        <ModifierModal
          item={modifierModalItem}
          onConfirm={handleModifierConfirm}
          onCancel={() => setModifierModalItem(null)}
        />
      )}

      {/* Allergen Selection Modal */}
      {showAllergenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-[90%] max-w-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Guest Allergies
              </h3>
              <button
                onClick={() => setShowAllergenModal(false)}
                className="p-1 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-500 mb-3">
              Select allergens for this table. This will alert kitchen staff.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {ALLERGEN_OPTIONS.map((allergen) => (
                <button
                  key={allergen}
                  onClick={() => toggleAllergen(allergen)}
                  className={clsx(
                    'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    guestAllergens.includes(allergen)
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  )}
                >
                  {allergen}
                </button>
              ))}
            </div>

            {guestAllergens.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-700 font-medium mb-1">Active Allergies:</p>
                <p className="text-sm text-red-800 font-bold">{guestAllergens.join(', ')}</p>
              </div>
            )}

            <button
              onClick={() => setShowAllergenModal(false)}
              className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Course Manager Modal */}
      {showCourseManager && checkId && (
        <CourseManager
          checkId={checkId}
          tableNumber={tableNumber || tableId}
          onClose={() => setShowCourseManager(false)}
        />
      )}

      {/* Void sent item — quick reason, then it's removed everywhere */}
      {voidTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6">
            {voidNeedsPin ? (
              <>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Manager approval needed</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Removing a sent item needs a manager — enter a manager PIN.
                </p>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={voidPin}
                  onChange={(e) => setVoidPin(e.target.value)}
                  placeholder="Manager PIN"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-center text-xl tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setVoidNeedsPin(null);
                      setVoidPin('');
                    }}
                    disabled={voidItemMutation.isPending}
                    className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => voidItemMutation.mutate({ itemId: voidTarget.id, reason: voidNeedsPin, managerPin: voidPin })}
                    disabled={voidPin.length < 4 || voidItemMutation.isPending}
                    className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-semibold disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  Remove {voidTarget.name}?
                </h3>
                <p className="text-slate-500 text-sm mb-4">
                  It comes off the check and the kitchen is told to stop making it. Why is it coming off?
                </p>
                <div className="space-y-2 mb-4">
                  {['Guest changed their mind', 'Entered by mistake', 'Kitchen ran out'].map((reason) => (
                    <button
                      key={reason}
                      disabled={voidItemMutation.isPending}
                      onClick={() => voidItemMutation.mutate({ itemId: voidTarget.id, reason })}
                      className="w-full py-3 px-4 bg-slate-50 text-slate-800 rounded-xl font-medium text-left active:bg-slate-100 disabled:opacity-50"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setVoidTarget(null)}
                  disabled={voidItemMutation.isPending}
                  className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold disabled:opacity-50"
                >
                  Keep it
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
